import axios from 'axios'

const CSRF_COOKIE_NAME = 'XSRF-TOKEN'
const CSRF_HEADER_NAME = 'X-XSRF-TOKEN'
const CSRF_COOKIE_ENDPOINT = '/v1/csrf-cookie'
const LOGIN_ENDPOINT = '/v1/login'
const MUTATING_METHODS = ['post', 'put', 'patch', 'delete']
const shouldUseLocalApiProxy =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_USE_LOCAL_API_PROXY === 'true'
const apiBaseURL = shouldUseLocalApiProxy
  ? ''
  : `${process.env.REACT_APP_BASE_URL_DEV}`

let csrfRequestPromise = null

const readCookie = (cookieName) => {
  if (typeof document === 'undefined' || !document.cookie) {
    return null
  }

  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${cookieName}=`))

  if (!cookie) {
    return null
  }

  const cookieValue = cookie.substring(cookieName.length + 1)
  const unwrappedCookieValue = cookieValue.replace(/^"|"$/g, '')

  try {
    return decodeURIComponent(unwrappedCookieValue)
  } catch (error) {
    return unwrappedCookieValue
  }
}

const shouldAttachCsrfToken = (config = {}) => {
  const method = (config.method || 'get').toLowerCase()
  const requestUrl = config.url || ''
  const normalizedUrl = requestUrl.startsWith('http')
    ? new URL(requestUrl).pathname
    : requestUrl

  if (!MUTATING_METHODS.includes(method)) {
    return false
  }

  return normalizedUrl !== CSRF_COOKIE_ENDPOINT
}

const ensureCsrfCookie = async () => {
  if (!csrfRequestPromise) {
    csrfRequestPromise = axios({
      url: CSRF_COOKIE_ENDPOINT,
      method: 'get',
      baseURL: apiBaseURL,
      withCredentials: true,
      headers: {
        Accept: 'application/json'
      }
    }).then((response) => {
      if (response.status !== 204) {
        throw new Error(`Unexpected CSRF cookie response status: ${response.status}`)
      }

      return response
    }).finally(() => {
      csrfRequestPromise = null
    })
  }

  await csrfRequestPromise
}

const axiosInstance = axios.create({
  baseURL: apiBaseURL,
  withCredentials: true,
  xsrfCookieName: CSRF_COOKIE_NAME,
  xsrfHeaderName: CSRF_HEADER_NAME
})

axiosInstance.interceptors.request.use(async (config) => {
  if (!shouldAttachCsrfToken(config)) {
    return {
      ...config,
      withCredentials: true
    }
  }

  let csrfToken = readCookie(CSRF_COOKIE_NAME)

  if (!csrfToken || config.url === LOGIN_ENDPOINT) {
    await ensureCsrfCookie()
    csrfToken = readCookie(CSRF_COOKIE_NAME)
  }

  return {
    ...config,
    withCredentials: true,
    headers: {
      ...(config.headers || {}),
      ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {})
    }
  }
})

export default axiosInstance